import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, MessageCircle, X, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Banner amarelo exibido no topo do Layout quando a licença da empresa ativa está
 * a ≤7 dias de vencer. Ignora quando:
 *  - Não há currentEmpresa (ex: SA em /admin/*)
 *  - Licença bloqueada (LicenseGate já cobre com tela cheia)
 *  - days_remaining > 7 ou undefined
 *  - Usuário já fechou (session-scoped via sessionStorage)
 *
 * Fetch de support-info uma vez pra montar link WhatsApp com msg pré-preenchida.
 */
export function LicenseExpiryBanner() {
  const { currentEmpresa } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("license_expiry_banner_dismissed") === "true"; }
    catch { return false; }
  });
  const [support, setSupport] = useState({ whatsapp_suporte: "", mensagem_padrao: "" });

  const status = currentEmpresa?.license_status;
  const d = status?.days_remaining;
  const shouldShow = !dismissed
    && status
    && !status.blocked
    && typeof d === "number"
    && d >= 0
    && d <= 7;

  useEffect(() => {
    if (!shouldShow) return;
    api.get("/license/support-info")
      .then((r) => setSupport({
        whatsapp_suporte: r.data?.whatsapp_suporte || "",
        mensagem_padrao: r.data?.mensagem_padrao || "",
      }))
      .catch(() => { /* silencioso — botão WhatsApp fica escondido */ });
  }, [shouldShow]);

  const wppHref = useMemo(() => {
    const digits = (support.whatsapp_suporte || "").replace(/\D/g, "");
    if (digits.length < 10) return null;
    const msg = support.mensagem_padrao
      || `Olá! A licença da empresa *${currentEmpresa?.nome || ""}* vence em ${d} dia(s). Poderia me ajudar a regularizar antes do bloqueio?`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  }, [support, currentEmpresa?.nome, d]);

  const dismiss = () => {
    try { sessionStorage.setItem("license_expiry_banner_dismissed", "true"); } catch { /* noop */ }
    setDismissed(true);
  };

  if (!shouldShow) return null;

  // Tons distintos conforme urgência
  const urgent = d <= 1;
  const wrapCls = urgent
    ? "bg-rose-100 border-rose-300 text-rose-900"
    : "bg-amber-100 border-amber-300 text-amber-900";
  const iconCls = urgent ? "text-rose-700" : "text-amber-700";
  const label = d === 0
    ? "Sua licença vence HOJE"
    : d === 1
      ? "Sua licença vence AMANHÃ"
      : `Sua licença vence em ${d} dias`;

  return (
    <div
      className={`border-b ${wrapCls} px-4 py-2.5 flex items-center gap-3 text-sm font-medium`}
      data-testid="license-expiry-banner"
      data-urgency={urgent ? "high" : "medium"}
    >
      {urgent
        ? <AlertTriangle className={`w-5 h-5 ${iconCls} shrink-0 animate-pulse`} />
        : <Clock className={`w-5 h-5 ${iconCls} shrink-0`} />}
      <div className="flex-1 min-w-0">
        <span className="font-bold" data-testid="license-expiry-banner-label">{label}</span>
        <span className="ml-2 opacity-80">— regularize com o suporte antes do bloqueio.</span>
      </div>
      {wppHref && (
        <a
          href={wppHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg shrink-0"
          data-testid="license-expiry-banner-whatsapp"
        >
          <MessageCircle className="w-4 h-4" />Falar com Suporte
        </a>
      )}
      <button
        type="button"
        onClick={dismiss}
        className={`p-1 rounded hover:bg-black/10 shrink-0 ${iconCls}`}
        title="Fechar aviso"
        aria-label="Fechar aviso"
        data-testid="license-expiry-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
